/**
 * Metapopulation SEIR — multiple regions coupled by a mobility OD
 * matrix. The natural couple of `@biomax/plugin-region-database` +
 * `@biomax/plugin-mobility` + the per-region SEIR system.
 *
 * Two-level dynamics:
 *   1. Within-region SEIR (β / σ / γ) runs independently per region.
 *   2. Between-region coupling moves a fraction of S, E, I, R between
 *      regions per day according to the OD matrix (rows normalised to
 *      a daily-outflow rate).
 *
 * The OD matrix `T[i][j]` represents persons/day flowing from region
 * i to region j. We treat it as a movement-rate matrix; effective per-
 * person daily transfer rate is `T[i][j] / N[i]`. Within a single time
 * step dt, S, E, I, R from region i are split proportionally and a
 * fraction sent to region j.
 *
 * Conservation: total population per region drifts slightly each step
 * because in-flow ≠ out-flow generally (one-way mobility OK). For
 * commuter-style two-way mobility, pre-symmetrise T (T_sym = (T + T^T)/2).
 *
 * Reference: GLEAM (Balcan + Vespignani 2010 PNAS) for metapopulation
 * pattern; this is a simplified deterministic version.
 */

import { integrateRk4 } from './rk4.js';
import { ENGINE_VERSION } from './sir.js';
import type { EpiResult, BetaScaler } from './types.js';

export interface MetaRegionInit {
  /** Population in the region. */
  N: number;
  /** Initial infectious. */
  I0: number;
  /** Initial exposed (default 0). */
  E0?: number;
  /** Initial recovered (default 0). */
  R0?: number;
}

export interface MetapopulationParams {
  /** Per-region initial conditions. */
  regions: MetaRegionInit[];
  /** Per-region transmission rate. β[i] = R0 × γ. */
  betaPerRegion: number[];
  /** Per-region recovery rate. */
  gammaPerRegion: number[];
  /** Per-region latent rate. */
  sigmaPerRegion: number[];
  /**
   * Origin-destination matrix T[i][j] = persons/day i → j. Diagonal
   * is zero (or ignored). Pre-symmetrise for commuter-style mobility.
   */
  odMatrix: number[][];
  /**
   * Mobility scale: multiplier on the OD matrix entries. 1.0 =
   * literal OD figures; 0 = closed borders; values > 1 model
   * holiday-season surge. Time-varying via mobilityScaler.
   */
  mobilityScale?: number;
  durationDays: number;
  dtDays?: number;
}

export interface MetapopulationOptions {
  /** Per-region time-varying β scaler. Indexed by region. */
  perRegionBetaScaler?: BetaScaler[];
  /** Global mobility scaler — multiplies all OD entries at time t. */
  mobilityScaler?: (t: number) => number;
}

export function simulateMetapopulation(
  params: MetapopulationParams, opts: MetapopulationOptions = {},
): EpiResult {
  const M = params.regions.length;
  if (M === 0) throw new Error('metapopulation: at least one region required');
  if (params.odMatrix.length !== M) throw new Error('odMatrix region-count mismatch');
  for (let i = 0; i < M; i++) {
    if (params.odMatrix[i]!.length !== M) throw new Error(`odMatrix row ${i} wrong length`);
  }
  if (params.betaPerRegion.length !== M) throw new Error('betaPerRegion length mismatch');
  if (params.gammaPerRegion.length !== M) throw new Error('gammaPerRegion length mismatch');
  if (params.sigmaPerRegion.length !== M) throw new Error('sigmaPerRegion length mismatch');

  const dt = params.dtDays ?? 0.5;
  const baseMobility = params.mobilityScale ?? 1.0;
  const mobScaler = opts.mobilityScaler ?? (() => 1);
  const t0 = performance.now();

  // State: per region [S, E, I, R], flattened. Index: comp k of region i = 4*i + k.
  const initial: number[] = new Array(4 * M).fill(0);
  for (let i = 0; i < M; i++) {
    const r = params.regions[i]!;
    const E0 = r.E0 ?? 0;
    const R0 = r.R0 ?? 0;
    const S0 = r.N - r.I0 - E0 - R0;
    if (S0 < 0) throw new Error(`region ${i}: N < I0+E0+R0`);
    initial[4 * i + 0] = S0;
    initial[4 * i + 1] = E0;
    initial[4 * i + 2] = r.I0;
    initial[4 * i + 3] = R0;
  }

  const f = (t: number, y: number[]): number[] => {
    const dy = new Array<number>(4 * M).fill(0);
    const mobMul = baseMobility * mobScaler(t);

    // Track current per-region totals for normalisation.
    const Ntot = new Array<number>(M);
    for (let i = 0; i < M; i++) {
      Ntot[i] = y[4 * i + 0]! + y[4 * i + 1]! + y[4 * i + 2]! + y[4 * i + 3]!;
    }

    // Within-region SEIR dynamics.
    for (let i = 0; i < M; i++) {
      const S = y[4 * i + 0]!, E = y[4 * i + 1]!, I = y[4 * i + 2]!;
      const Ni = Ntot[i]!;
      if (Ni === 0) continue;
      const betaScalerI = opts.perRegionBetaScaler?.[i];
      const betaT = betaScalerI ? params.betaPerRegion[i]! * betaScalerI(t) : params.betaPerRegion[i]!;
      const newExposed = (betaT * S * I) / Ni;
      const becomeInfectious = params.sigmaPerRegion[i]! * E;
      const recoveries = params.gammaPerRegion[i]! * I;
      dy[4 * i + 0] += -newExposed;
      dy[4 * i + 1] += newExposed - becomeInfectious;
      dy[4 * i + 2] += becomeInfectious - recoveries;
      dy[4 * i + 3] += recoveries;
    }

    // Between-region coupling: move S, E, I, R proportionally.
    for (let i = 0; i < M; i++) {
      const Ni = Ntot[i]!;
      if (Ni === 0) continue;
      for (let j = 0; j < M; j++) {
        if (i === j) continue;
        const rate = (params.odMatrix[i]![j]! * mobMul) / Ni;  // per-person daily transfer rate
        if (rate === 0) continue;
        // Move S, E, I, R proportionally.
        for (let k = 0; k < 4; k++) {
          const moved = rate * y[4 * i + k]!;
          dy[4 * i + k] -= moved;
          dy[4 * j + k] += moved;
        }
      }
    }

    return dy;
  };

  const integrated = integrateRk4(f, initial, params.durationDays, dt);

  const compartments: Record<string, number[]> = { S: [], E: [], I: [], R: [] };
  for (let i = 0; i < M; i++) {
    compartments[`S_${i}`] = [];
    compartments[`E_${i}`] = [];
    compartments[`I_${i}`] = [];
    compartments[`R_${i}`] = [];
  }
  for (const row of integrated.y) {
    let sumS = 0, sumE = 0, sumI = 0, sumR = 0;
    for (let i = 0; i < M; i++) {
      const S = row[4 * i + 0]!, E = row[4 * i + 1]!, I = row[4 * i + 2]!, R = row[4 * i + 3]!;
      compartments[`S_${i}`]!.push(S);
      compartments[`E_${i}`]!.push(E);
      compartments[`I_${i}`]!.push(I);
      compartments[`R_${i}`]!.push(R);
      sumS += S; sumE += E; sumI += I; sumR += R;
    }
    compartments.S!.push(sumS);
    compartments.E!.push(sumE);
    compartments.I!.push(sumI);
    compartments.R!.push(sumR);
  }

  return {
    t: integrated.t,
    compartments,
    receipt: {
      engineVersion: ENGINE_VERSION,
      seed: 'metapopulation-seir',
      elapsedMs: performance.now() - t0,
      steps: integrated.steps,
    },
  };
}

/**
 * Symmetrise an OD matrix to (T + T^T) / 2 — useful when the source
 * matrix is one-way (e.g., aviation, migration) but you want a
 * conservation-preserving commuter-flow approximation.
 */
export function symmetriseODMatrix(T: number[][]): number[][] {
  const M = T.length;
  const out: number[][] = Array.from({ length: M }, () => new Array(M).fill(0));
  for (let i = 0; i < M; i++) {
    for (let j = 0; j < M; j++) {
      out[i]![j] = (T[i]![j]! + T[j]![i]!) / 2;
    }
  }
  return out;
}
